import Phaser from 'phaser';
import type { FactionId, Subject } from '../../shared/types';
import { FACTIONS, factionForSubject } from '../../shared/factions';
import { loadProgress, saveProgress, recordBossDefeated, getSelectedHeroId } from '../../shared/progress';
import { HubBuilding } from '../entities/HubBuilding';
import { ParallaxSystem } from './ParallaxSystem';
import { JuiceLayer } from './JuiceLayer';
import { AudioBus } from '../audio/AudioBus';
import { playerTankKeyFor, PLAYER_TANK_SPRITE_KEYS, PLAYER_TANK_SPRITE_URLS } from '../entities/sprite-keys';
import { resolveTankRender } from '../entities/tank-atlas';
import { ASSETS } from '../config';
import { pickBossById } from '../entities/boss-pool';
import { computeCombatPower } from '../progression/combat-power';

/**
 * ⚠️ 已停用(boss 2026-06-14):闖關地圖(CampaignMapScene)為唯一主入口。
 * 此「選門大廳」HubScene 已從主流程 orphan —— 沒有任何 setMode / scene.start 路由到它
 * (main.ts 的 'hub'/'campaign-map' mode 已改 start CampaignMapScene;CampaignMapScene
 * 返回 → home;result-scene 結算 fallback 改回闖關地圖)。保留檔案 + boot scene 註冊
 * (避免連鎖破壞),供日後恢復「自由戰模式」用。動此檔前先確認是否真要恢復自由戰入口。
 *
 * W7 — HubScene (主場景選關大廳).
 *
 * Player は academy で修煉値を稼ぎ、HBattle CTA 経由で hub へ到達。Hub は 5
 * 門派建築物 + 中央広場の俯瞰マップ。各建築物クリック → 対応 boss 戦。
 *
 * **學習與遊戲分離鐵則**: HubScene 內 0 題目,只能 navigate (→ HorizontalBattleScene
 * or back → window.location.href '/'。)
 *
 * Coords (800×600 canvas):
 *   celestial 200,160 / runic 400,140 / techno 600,160
 *   elemental 160,400 / dominion 640,400 / 中央広場 tank 400,310
 */
export class HubScene extends Phaser.Scene {
  /** Faction id → HubBuilding instance, 用於 hover / shake / showConquered。 */
  readonly buildings: Partial<Record<FactionId, HubBuilding>> = {};

  /** Subject inverse-lookup util for isFactionLocked。 */
  private static subjectForFaction(factionId: FactionId): Subject {
    const map: Record<FactionId, Subject> = {
      celestial: 'math',
      runic: 'chinese',
      techno: 'english',
      elemental: 'nature',
      dominion: 'social',
    };
    return map[factionId];
  }

  /** Static export 供外部 / test access。 */
  static getSubjectForFaction(factionId: FactionId): Subject {
    return HubScene.subjectForFaction(factionId);
  }

  /**
   * faction-unlock (老闆 2026-06-03「進去都是星算門 / 遇不到指定魔王」):
   * 五門派全部解鎖,玩家在 hub 點哪門就打哪門(對應魔王/地圖),不必碰 URL 參數。
   *
   * ⚠️ 這暫時關閉了「學習產出修煉值才解鎖戰力」鐵則(原邏輯保留在下方註解)。
   * 現階段是給開發者小孩試玩的 MVP,優先讓五門派/五魔王/五地圖都玩得到。日後要恢復
   * 「學習解鎖」時,把下面 return false 換回被註解的原邏輯即可。
   */
  static isFactionLocked(_factionId: FactionId): boolean {
    return false; // 全解鎖
    // ── 原「學習解鎖」邏輯(日後恢復用) ──────────────────────────────
    // let p;
    // try { p = loadProgress(); } catch { p = null; }
    // if (!p) return _factionId !== 'celestial';
    // const subject = HubScene.subjectForFaction(_factionId);
    // const hasUnlockedSkill = (p.unlockedSkills ?? []).some(
    //   (id: string) => id.startsWith(_factionId));
    // const hasBox3KP = (p.studentKPs ?? []).some(
    //   (kp) => kp.subject === subject && kp.box >= 3);
    // if (hasUnlockedSkill || hasBox3KP) return false;
    // return _factionId !== 'celestial';
  }

  /** Five faction coords (依 spec §5 表)。 */
  static readonly BUILDING_COORDS: Record<FactionId, { x: number; y: number }> = {
    celestial: { x: 200, y: 160 },
    runic:     { x: 400, y: 140 },
    techno:    { x: 600, y: 160 },
    elemental: { x: 160, y: 400 },
    dominion:  { x: 640, y: 400 },
  };

  static readonly CENTER_PLAZA = { x: 400, y: 310 };
  /** 中央預覽坦克顯示高度(px)。boss 2026-06-13「放太大了」:96 → 76,跟戰鬥端一起變小。 */
  static readonly HUB_TANK_H = 76;

  /** Boss display label per faction(spec §6.1)— hover tooltip 用。 */
  private static readonly BOSS_LABELS: Record<FactionId, string> = {
    celestial: '數獸',
    runic:     '文字魔',
    techno:    '機械鬼',
    elemental: '元素獸',
    dominion:  '域主',
  };

  /** Unlocked hint sentence per faction — hover tooltip 上的第 2 行 hint。 */
  private static readonly UNLOCKED_HINTS: Record<FactionId, string> = {
    celestial: '精準爆發,適合一擊清線',
    runic:     '防禦控制,適合穩定推進',
    techno:    '速度偵察,適合機動拉開',
    elemental: '元素持續,適合纏鬥消耗',
    dominion:  '資源支援,適合長線壓制',
  };

  /** Locked reason 文案 — 對應 isFactionLocked 判定的明確 unlock 條件。 */
  private static readonly LOCKED_REASON = '需先在修煉學院升 box ≥ 3';

  private parallax: ParallaxSystem | null = null;
  /** JuiceLayer for future hub-side polish hooks (scalePunch / sparkle).
   *  W7 ships infra-only; concrete call sites land in W8. */
  private juice: JuiceLayer | null = null;
  private audio: AudioBus | null = null;
  private tooltip: Phaser.GameObjects.Text | null = null;
  private toast: Phaser.GameObjects.Text | null = null;

  constructor() {
    super({ key: 'HubScene' });
  }

  /**
   * W8 PR-4 — 嘗試 load 5 faction hub-building atlas image。
   * 真圖未到位時 Phaser loader 靜默 404 → HubBuilding constructor
   * 的 textures.exists() 回 false → fallback 回 W7 Rectangle placeholder。
   *
   * W1-3 根因修復 (2026-06-12, boss 2026-06-06「中央 emoji 🚗 紅車很出戲」):
   * hub 由 main.ts setMode('hub') 直接 scene.start('HubScene'),完全不經過
   * HorizontalBattleScene / TitleScene 的 preload — 戰車 atlas 與 per-file
   * texture 從未被請求(prod 實證:textures.exists('tanks-atlas')===false、
   * network 連 404 都沒有),所以 resolveTankRender 永遠回 null → 中央主角
   * 永遠走 emoji fallback。修法:HubScene 自己 preload 同一份 tanks atlas +
   * per-file fallback PNG(同 key 同 URL,Phaser 以 texture key dedup,之後
   * 進戰鬥場景不會重複下載)。
   */
  preload(): void {
    try { HubBuilding.preloadAtlas(this); } catch { /* headless */ }
    try { HubScene.preloadTankSprites(this); } catch { /* headless */ }
  }

  /**
   * 載入中央主角戰車所需 texture:tanks atlas(首選)+ 5 張 per-file 玩家戰車
   * PNG(atlas 缺檔時的後備)。與 HorizontalBattleScene.preload 同 key 同 URL。
   * 已存在的 texture 直接跳過(re-entry / 從戰鬥返回 hub 時不重載)。
   * 全 try-guard,headless test harness(無 loader plugin)安全。
   */
  static preloadTankSprites(scene: Phaser.Scene): void {
    try {
      const tx = (scene as { textures?: { exists?: (k: string) => boolean } }).textures;
      const loader = (scene as unknown as {
        load?: {
          atlas?: (k: string, png: string, json: string) => void;
          image?: (k: string, u: string) => void;
        };
      }).load;
      if (!loader) return; // headless / no loader plugin
      // 1. Atlas — single draw-call path, same as battle scene。
      try {
        if (!tx?.exists?.(ASSETS.tanksAtlas) && typeof loader.atlas === 'function') {
          loader.atlas(ASSETS.tanksAtlas, 'assets/atlas/tanks.png', 'assets/atlas/tanks.json');
        }
      } catch { /* per-entry guard */ }
      // 2. Per-file fallbacks — only needed when atlas 404s (stripped build)。
      if (typeof loader.image !== 'function') return;
      for (const [faction, key] of Object.entries(PLAYER_TANK_SPRITE_KEYS)) {
        try {
          if (tx?.exists?.(key)) continue; // already loaded
          loader.image(key, PLAYER_TANK_SPRITE_URLS[faction as FactionId]);
        } catch { /* per-entry guard */ }
      }
    } catch { /* headless */ }
  }

  create(): void {
    // 1. Parallax bg — hub neutral color 0x1a2030
    try {
      this.parallax = new ParallaxSystem(this);
      this.parallax.create(0x1a2030);
    } catch { /* headless */ }

    // 1b. 基地地面層 — 微美化:深底色 + 校園「地坪」漸層帶 + 連向中央的路徑線,
    //     讓 5 棟建築不再像漂浮在純色背景上。佈局座標不動。
    try { this._placeGround(); } catch { /* headless */ }

    // 1c. 標題 — 讓小孩一眼看懂「這裡是選門派出戰」。置於 nav 下方,不擋建築。
    try { this._buildTitle(); } catch { /* headless */ }

    // 2. 5 building 配置
    try { this._placeBuildings(); } catch { /* headless */ }

    // 3. 中央広場 player tank
    try { this._placePlayerTank(); } catch { /* headless */ }

    // 4. 上部 nav bar (Phaser-side; DOM overlay 留給整合層)
    try { this._buildTopNav(); } catch { /* headless */ }

    // 4a. 戰力分數徽章(R-004 A2)— 學習驅動戰力,讓「越打越強」在 hub 看得見。
    //     唯讀 computeCombatPower(progress);不寫任何欄位(學習/遊戲分離鐵則)。
    try { this._buildCombatPowerBadge(); } catch { /* headless */ }

    // 4b. 「出戰(闖關)」CTA — 老闆 2026-06-06「沒有闖關的感覺」→ 進線性關卡地圖
    //     (CampaignMapScene)。5 門派建築維持「自由戰」入口不動;這顆是主推進。
    try { this._buildCampaignCta(); } catch { /* headless */ }

    // 5. BGM stub
    try {
      this.audio = new AudioBus();
      this.audio.playMusic('hub-ambient', true);
    } catch { /* headless */ }

    // 6. JuiceLayer
    try { this.juice = new JuiceLayer(this); } catch { /* headless */ }

    // 7. Boss defeat return handler (init data)
    try {
      const data = (this.scene as unknown as { settings?: { data?: { defeatedFaction?: FactionId } } }).settings?.data;
      if (data?.defeatedFaction) {
        // 在 update tick 後執行,避免 building 還沒 mount 完。
        try {
          this.time.delayedCall(0, () => {
            this.markFactionConquered(data.defeatedFaction!);
          });
        } catch {
          // headless: 直接 call。
          this.markFactionConquered(data.defeatedFaction);
        }
      }
    } catch { /* headless */ }

    // 8. Mastery W4 — 學習端 chapter-mastered listener。
    //    透過 window CustomEvent 跨層接收,不 import 任何 learn/ 模組(保持
    //    學習與遊戲分離鐵則)。listener 在 shutdown() 移除以避免泄漏。
    try {
      if (typeof window !== 'undefined') {
        window.addEventListener('chapter:mastered', this._chapterMasteredHandler);
      }
    } catch { /* headless */ }
  }

  /** Bound handler stored so shutdown() can remove the same reference. */
  private readonly _chapterMasteredHandler = (raw: Event): void => {
    try {
      const ev = raw as CustomEvent<{ chapterId?: string }>;
      const cid = ev.detail?.chapterId;
      if (!cid) return;
      this.markChapterConquered(cid);
    } catch { /* headless or detached scene */ }
  };

  /**
   * 基地地面層 — 純裝飾,墊在 parallax 之上、建築/avatar 之下 (depth -5)。
   * 校園「地坪」感:深色地面帶 + 一條從中央廣場放射向 5 棟建築的「步道」線,
   * 讓玩家視線自然從建築被導引到中央主角戰車。全 try-guard,headless 安全。
   * ⚠️ 不改任何 BUILDING_COORDS / CENTER_PLAZA,純加視覺。
   */
  private _placeGround(): void {
    const center = HubScene.CENTER_PLAZA;

    // 0. 全幅底色 — 蓋滿整張 800×600 畫布,杜絕「右側純黑沒畫到」的破洞感
    //    (boss before-shot:techno/dominion 右側一塊黑)。深夜藍當天幕基色。
    try {
      const base = this.add.rectangle(400, 300, 800, 600, 0x0e1422, 1);
      try { base.setDepth(-9); } catch { /* noop */ }
    } catch { /* headless */ }

    // 0b. 天幕漸層 — 用上中下三條半透明色帶疊出「夜空→地面」的縱向漸層,
    //     上方偏冷藍、下方偏深(無真 gradient API,堆疊矩形是最穩做法)。
    try {
      const sky = this.add.rectangle(400, 150, 800, 300, 0x1a2740, 0.55);
      try { sky.setDepth(-8); } catch { /* noop */ }
      const horizon = this.add.rectangle(400, 360, 800, 160, 0x162035, 0.5);
      try { horizon.setDepth(-8); } catch { /* noop */ }
      const ground = this.add.rectangle(400, 500, 800, 220, 0x10182a, 0.7);
      try { ground.setDepth(-8); } catch { /* noop */ }
    } catch { /* headless */ }

    // 0c. 散落星點(裝飾)— 天幕上幾顆靜態小亮點,讓夜空不死板。reduceMotion
    //     不影響(靜態)。用 circle,精簡 test stub 缺 circle 時靜默跳過。
    try {
      const stars: Array<[number, number, number]> = [
        [90, 70, 2], [180, 48, 1.5], [320, 80, 2], [520, 56, 1.5],
        [690, 74, 2], [740, 120, 1.5], [60, 150, 1.5], [760, 200, 2],
      ];
      for (const [sx, sy, sr] of stars) {
        const s = this.add.circle(sx, sy, sr, 0xcfe0ff, 0.5);
        try { s.setDepth(-7); } catch { /* noop */ }
      }
    } catch { /* headless */ }

    // 中央廣場暗環(地坪上的「廣場」鋪面)。
    try {
      const plazaOuter = this.add.circle(center.x, center.y, 168, 0x1c2a48, 0.35);
      try { plazaOuter.setDepth(-6); } catch { /* noop */ }
      const plaza = this.add.circle(center.x, center.y, 132, 0x22335a, 0.45);
      try { plaza.setDepth(-5); } catch { /* noop */ }
    } catch { /* headless */ }

    // 從中央放射向 5 棟建築的「能量步道」線 — 用門派色,讓視線從中央連到各門。
    try {
      const factionIds: FactionId[] = ['celestial', 'runic', 'techno', 'elemental', 'dominion'];
      for (const id of factionIds) {
        const b = HubScene.BUILDING_COORDS[id];
        let col = 0x3a4a6e;
        try { col = FACTIONS[id]?.colorPrimary ?? col; } catch { /* noop */ }
        const line = this.add.line(0, 0, center.x, center.y, b.x, b.y, col, 0.4);
        try { line.setOrigin(0, 0); } catch { /* noop */ }
        try { line.setLineWidth?.(3); } catch { /* noop */ }
        try { line.setDepth(-5); } catch { /* noop */ }
      }
    } catch { /* headless */ }
  }

  /**
   * 標題列 — 「選擇門派 · 點門出戰」。置於 top nav(y≈24)下方 y≈62,置中。
   * 純展示,不擋任何建築(建築最高在 y≈90)。reduceMotion 不影響(靜態)。
   */
  private _buildTitle(): void {
    try {
      // runic 卡片頂在 y≈70(中心 140 − 半高 70),副標縮小置於 nav 與卡片間的
      // 窄帶,y=58、字級 15,確保不壓到 runic 卡頂。
      const t = this.add.text(400, 58, '選擇門派 · 點門出戰', {
        fontSize: '15px', fontFamily: 'sans-serif', color: '#ffe9a8',
      });
      try { t.setOrigin(0.5); } catch { /* noop */ }
      try { t.setDepth(20); } catch { /* noop */ }
    } catch { /* headless */ }
  }

  private _placeBuildings(): void {
    const factionIds: FactionId[] = ['celestial', 'runic', 'techno', 'elemental', 'dominion'];
    for (const id of factionIds) {
      const coord = HubScene.BUILDING_COORDS[id];
      const locked = HubScene.isFactionLocked(id);
      const building = new HubBuilding(this, coord.x, coord.y, id, locked);
      this.buildings[id] = building;
      // Click handler — pointerdown on container。
      try {
        building.container.on('pointerdown', () => this.onBuildingClick(id));
        building.container.on('pointerover', () => this._showTooltip(id));
        building.container.on('pointerout',  () => this._hideTooltip());
      } catch { /* headless */ }
    }
  }

  /**
   * 中央広場 — 玩家「基地停機坪」+ 當前主角戰車 sprite。
   *
   * avatar 換法 (老闆 2026-06-06「中央 emoji 🚗 紅車很出戲」):
   *   讀玩家選的英雄 `getSelectedHeroId(progress)` (fallback 'celestial'),用
   *   `playerTankKeyFor(heroId)` → `resolveTankRender` 解出 atlas frame
   *   ('tanks-atlas' frame) 或 per-file texture ('tank-player-{id}'),跟戰鬥
   *   場景 (HorizontalBattleScene) 同一份來源,所以 hub 中央那台 = 戰鬥開的那台。
   *   側視坦克本來就朝右,合理大小 (~96px 高、保留長寬比)。缺圖才 fallback emoji。
   *
   * 視覺微美化:停機坪 (圓形基座 + 同心暈光 + 朝向 H/V 線的「跑道」感),讓中央
   *   區域更像「基地/校園廣場」,不只是一個灰矩形。佈局座標 (CENTER_PLAZA) 不動。
   */
  private _placePlayerTank(): void {
    const { x, y } = HubScene.CENTER_PLAZA;

    // 中央 plaza「停機坪」底座 — 取代舊的單一灰矩形。
    // 由外到內疊三層,headless 下任一 add 失敗都各自 try-guard。
    const heroId = this._currentHeroId();
    let accent = 0x4a90d9; // 中性藍 fallback
    try { accent = FACTIONS[heroId]?.colorPrimary ?? accent; } catch { /* noop */ }

    try {
      // 外圈暈光(門派色,低透明)— 讓中央有「主角光環」感。
      const halo = this.add.circle(x, y, 92, accent, 0.14);
      try { halo.setDepth(0); } catch { /* noop */ }
      // 暈光呼吸 tween,微幅 scale 讓畫面不死板。
      try {
        this.tweens.add({
          targets: halo, scaleX: 1.08, scaleY: 1.08,
          duration: 1800, yoyo: true, repeat: -1, ease: 'Sine.InOut',
        });
      } catch { /* headless */ }
    } catch { /* headless */ }
    try {
      // 停機坪圓盤主體 + 細邊框。
      const pad = this.add.circle(x, y, 72, 0x222c3e, 0.92);
      try { pad.setStrokeStyle?.(3, accent, 0.85); } catch { /* noop */ }
      try { pad.setDepth(0); } catch { /* noop */ }
    } catch {
      // 圓形不可用的環境 → 退回原本的矩形底座,確保中央仍有視覺中心。
      try { this.add.rectangle(x, y, 140, 100, 0x2a3548, 0.6); } catch { /* headless */ }
    }
    try {
      // 內圈降落標記圓。
      const inner = this.add.circle(x, y, 50, 0x000000, 0);
      try { inner.setStrokeStyle?.(2, accent, 0.4); } catch { /* noop */ }
      try { inner.setDepth(0); } catch { /* noop */ }
    } catch { /* headless */ }

    // 主角戰車 sprite — 隨「選的英雄」切換,跟戰鬥場景同一份來源。
    // resolveTankRender 優先 atlas frame,其次 per-file texture,皆無 → null → emoji。
    let tank: Phaser.GameObjects.GameObject | null = null;
    try {
      const key = playerTankKeyFor(heroId);
      const render = this.textures
        ? resolveTankRender(this.textures as unknown as { exists(k: string): boolean }, key)
        : null;
      if (render?.mode === 'atlas') {
        tank = this.add.image(x, y, render.atlasKey, render.frame) as unknown as Phaser.GameObjects.GameObject;
      } else if (render?.mode === 'image') {
        tank = this.add.image(x, y, render.key) as unknown as Phaser.GameObjects.GameObject;
      } else {
        // 資產齊全時不應走到這裡(preloadTankSprites 已載 atlas + per-file)。
        // 不再靜默 — 留 warn 讓 QA / console smoke 能立刻看到缺哪個 key。
        try {
          console.warn(
            `[HubScene] tank texture missing for hero '${heroId}' (key '${key}', atlas '${ASSETS.tanksAtlas}') — ` +
            'falling back to emoji placeholder. Check assets/atlas/tanks.* and assets/sprites/tank-player-*.png in the build.',
          );
        } catch { /* noop */ }
        const text = this.add.text(x, y, '🚗', { fontSize: '32px', fontFamily: 'sans-serif' });
        try { text.setOrigin(0.5); } catch { /* noop */ }
        tank = text;
      }
    } catch { /* headless */ }

    // 戰車外觀整理:置中、合理大小(~96px 高、保留長寬比)、朝右(側視原圖即朝右,
    // 不旋轉),疊在停機坪之上。emoji fallback 不套用 image-only 設定。
    if (tank) {
      const img = tank as unknown as {
        setOrigin?: (v: number) => unknown;
        setDepth?: (v: number) => unknown;
        setRotation?: (v: number) => unknown;
        setDisplaySize?: (w: number, h: number) => unknown;
        width?: number; height?: number;
      };
      try { img.setOrigin?.(0.5); } catch { /* noop */ }
      try { img.setDepth?.(2); } catch { /* noop */ }
      try { img.setRotation?.(0); } catch { /* noop */ }
      try {
        // 保留長寬比縮到 HUB_TANK_H 高(側視坦克通常較寬)。
        // boss 2026-06-13「放太大了」:中央預覽坦克 96 → 76,跟戰鬥端一起變小。
        const w = img.width ?? 0;
        const h = img.height ?? 0;
        if (typeof img.setDisplaySize === 'function' && w > 0 && h > 0) {
          img.setDisplaySize(HubScene.HUB_TANK_H * (w / h), HubScene.HUB_TANK_H);
        } else {
          img.setDisplaySize?.(HubScene.HUB_TANK_H, HubScene.HUB_TANK_H);
        }
      } catch { /* headless or emoji text */ }

      // Idle tween:y ±6,2 秒周期(原行為保留)。
      try {
        this.tweens.add({
          targets: tank,
          y: y - 6,
          duration: 1000,
          yoyo: true,
          repeat: -1,
          ease: 'Sine.InOut',
        });
      } catch { /* headless */ }
    }

    // 主角名牌 — 停機坪下方標出「現在出戰的主角」(門派 emoji + displayName)。
    // 純展示,不影響互動;座標在 plaza 下緣,不擋建築/nav。
    try {
      const f = FACTIONS[heroId];
      const label = `${f?.emoji ?? '🚀'} ${f?.displayName ?? '主角'}`;
      const nameTag = this.add.text(x, y + 86, label, {
        fontSize: '14px', fontFamily: 'sans-serif', color: '#e8eef7',
        backgroundColor: '#10131e', padding: { x: 8, y: 3 },
      });
      try { nameTag.setOrigin(0.5); } catch { /* noop */ }
      try { nameTag.setDepth(3); } catch { /* noop */ }
    } catch { /* headless */ }
  }

  /**
   * 讀玩家當前選的英雄門派 id,fallback 'celestial'。getSelectedHeroId 不可用
   * (極簡 stub) 或無 progress 時皆安全回 celestial。
   */
  private _currentHeroId(): FactionId {
    try {
      const p = loadProgress();
      if (p && typeof getSelectedHeroId === 'function') return getSelectedHeroId(p);
    } catch { /* headless / no localStorage */ }
    return 'celestial';
  }

  private _buildTopNav(): void {
    // 上部 bar 背景 + 「← 修煉學院」按鈕(spec §2)。
    try {
      this.add.rectangle(400, 24, 800, 48, 0x10131e, 0.85).setOrigin(0.5);
    } catch { /* headless */ }

    let backText: Phaser.GameObjects.Text | null = null;
    try {
      backText = this.add.text(80, 24, '← 修煉學院', {
        fontSize: '18px', fontFamily: 'sans-serif', color: '#FFD54F',
      });
      try { backText.setOrigin(0.5); } catch { /* noop */ }
      try {
        // Hu-P1-01: Apple HIG 44×44 minimum tap target — text bounds (~110×24)
        // is wider than 44 but only 24 tall. Force a 44-tall hit rect via
        // setSize so finger-tap-friendly even on iPad. Pivots on origin(0.5)。
        const HIT_W = Math.max(backText.width, 44);
        const HIT_H = 44;
        backText.setSize(HIT_W, HIT_H);
        backText.setInteractive({ useHandCursor: true });
        backText.on('pointerdown', () => this.onBackToAcademy());
      } catch { /* headless */ }
    } catch { /* headless */ }

    // 中央 nav 標題 — 取代舊的「✓星算 ✓符文…」鎖狀指示列(每張卡片現在自帶
    // 解鎖/通關視覺,nav 重覆顯示反而雜亂)。改放場景標題,kid 一眼看懂用途。
    // Hu-P1-02 的 wordWrap 慣例保留(避免長字串溢出 nav)。
    try {
      this.add.text(400, 24, '⚔️ 出戰大廳', {
        fontSize: '18px', fontFamily: 'sans-serif', color: '#e8eef7', fontStyle: 'bold',
        wordWrap: { width: 460, useAdvancedWrap: true },
        align: 'center',
      }).setOrigin(0.5);
    } catch { /* headless */ }
  }

  /**
   * 戰力分數徽章(R-004 A2)— top nav 右側顯示「⚡戰力 N」。
   *
   * 戰力由學習軌資料推導(computeCombatPower 唯讀 skillLevels),解鎖/升級技能
   * 後數字變大 → 在遊戲端也看得見「學習讓我更強」。遊戲風格金色 token
   * (#FFD54F),與 hub nav 一致。純展示,不寫任何 progress(鐵則)。
   * 全 try-guard,headless 安全。
   */
  private _buildCombatPowerBadge(): void {
    let power = 0;
    try {
      const p = loadProgress();
      power = computeCombatPower(p);
    } catch { /* headless / no localStorage → 0 */ }

    try {
      const t = this.add.text(720, 24, `⚡ 戰力 ${power}`, {
        fontSize: '16px', fontFamily: 'sans-serif', color: '#FFD54F', fontStyle: 'bold',
      });
      try { t.setOrigin(0.5); } catch { /* noop */ }
      try { t.setDepth(40); } catch { /* noop */ }
    } catch { /* headless */ }
  }

  /**
   * 「出戰 · 闖關」主 CTA — 進線性關卡地圖(CampaignMapScene)。
   *
   * 老闆 2026-06-06「感覺沒有闖關的感覺」→ 拍板 Candy Crush 線性關卡地圖。
   * 放在畫面底部置中,金色高亮,當 hub 的主推進入口;5 門派建築維持「自由戰」。
   *
   * 學習與遊戲分離鐵則:只 navigate,不彈題目。
   */
  private _buildCampaignCta(): void {
    const x = 400;
    const y = 560;
    let bg: Phaser.GameObjects.Rectangle | null = null;
    try {
      bg = this.add.rectangle(x, y, 240, 52, 0xffd54f, 1);
      try { bg.setStrokeStyle?.(3, 0xffffff, 1); } catch { /* noop */ }
      try { bg.setDepth(30); } catch { /* noop */ }
    } catch { /* headless */ }
    try {
      const t = this.add.text(x, y, '⚔️ 出戰 · 闖關', {
        fontSize: '22px', fontFamily: 'sans-serif', color: '#0a0f1e', fontStyle: 'bold',
      });
      try { t.setOrigin(0.5); } catch { /* noop */ }
      try { t.setDepth(31); } catch { /* noop */ }
    } catch { /* headless */ }
    if (bg) {
      try {
        bg.setInteractive({ useHandCursor: true });
        bg.on('pointerdown', () => this.onEnterCampaign());
      } catch { /* headless */ }
    }
  }

  /** 進闖關地圖。fade → start('CampaignMapScene')。只 navigate。 */
  onEnterCampaign(): void {
    const go = () => { try { this.scene.start('CampaignMapScene'); } catch { /* noop */ } };
    try {
      const cam = this.cameras?.main;
      const fadeOut = (cam as unknown as { fadeOut?: (ms: number, r?: number, g?: number, b?: number) => void })?.fadeOut;
      if (cam && typeof fadeOut === 'function') {
        fadeOut.call(cam, 250, 0, 0, 0);
        const once = (cam as unknown as { once?: (e: string, cb: () => void) => void }).once;
        if (typeof once === 'function') once.call(cam, 'camerafadeoutcomplete', go);
        else go();
      } else { go(); }
    } catch { go(); }
  }

  /**
   * Show hover tooltip — locked / unlocked 兩段文案。
   *
   * Spec W7 §6.1:tooltip 1 行包含 displayName + 對戰 boss label;polish 版
   * 改為 2 行,locked 顯 unlock 條件文案、unlocked 顯 faction hint。鎖頭背景
   * 用深紅 (#3a1010) 突出禁入感,unlocked 維持深黑 #000000 中性色。
   *
   * Tooltip 透過 _hideTooltip → destroy 清理,避免 leak。
   */
  private _showTooltip(factionId: FactionId): void {
    try {
      const f = FACTIONS[factionId];
      const label = HubScene.BOSS_LABELS[factionId];
      const locked = HubScene.isFactionLocked(factionId);
      const lineMain = `${f.displayName} — 對戰 boss ${label}`;
      const lineSub = locked
        ? HubScene.LOCKED_REASON
        : HubScene.UNLOCKED_HINTS[factionId];
      const msg = `${lineMain}\n${lineSub}`;
      const coord = HubScene.BUILDING_COORDS[factionId];
      this.tooltip = this.add.text(coord.x, coord.y + 80, msg, {
        fontSize: '14px',
        fontFamily: 'sans-serif',
        color: '#ffffff',
        backgroundColor: locked ? '#3a1010' : '#000000',
        padding: { x: 8, y: 5 },
        align: 'center',
      });
      try { this.tooltip.setOrigin(0.5); } catch { /* noop */ }
      try { this.tooltip.setDepth(50); } catch { /* noop */ }
    } catch { /* headless */ }
  }

  private _hideTooltip(): void {
    try { this.tooltip?.destroy(); } catch { /* noop */ }
    this.tooltip = null;
  }

  /**
   * Click handler — locked → shake + toast + screen-shake;unlocked → fade-out + start battle。
   *
   * Polish (W7 §6.2):locked click 加 W3 JuiceLayer.screenShake(0.005, 200)
   * 視覺反饋,讓「不能進」感更實體。intensity 0.005 = 「regular hit」級,不過
   * 度晃動干擾 hover/tooltip 閱讀。
   *
   * 注意:HubScene NEVER 彈出題目 — only navigate (學習與遊戲分離鐵則)。
   */
  onBuildingClick(factionId: FactionId): void {
    const locked = HubScene.isFactionLocked(factionId);
    const building = this.buildings[factionId];
    if (locked) {
      try { building?.shake(); } catch { /* noop */ }
      try { this.juice?.screenShake(0.005, 200); } catch { /* noop */ }
      try { this._showLockedToast(factionId); } catch { /* noop */ }
      return;
    }
    // Polish (W7 §6.3): unlocked → 在 fade 開始的瞬間 disable 全部 building
    // interactive 防 double-click 切兩次戰場;再 building.scalePunch 給「進入」
    // 視覺反饋,最後 camera fade-out → scene.start。
    try { this._disableAllBuildingInteractive(); } catch { /* noop */ }
    try {
      // scale punch:short scale-up to 1.12, springs back via Elastic.Out 280ms。
      this.juice?.scalePunch(building!.container as unknown as Phaser.GameObjects.GameObject, 1.12, 280);
    } catch { /* noop */ }

    // W7 §6.4 — Unlocked → cameras fade out → start battle scene。
    //
    // bossId resolution:hub building 一棟對應一個門派,內部 lookup 該門派
    // 第一隻 BOSS_POOL boss(techno/dominion 自動 fallback TIER2_BOSS_POOL)。
    // 若 lookup 失敗(BOSS_POOL 改名)仍將 factionId 當 bossId 傳下去,
    // HorizontalBattleScene.init() 端會再 lookup 一次,最差 fallback random。
    // hubFactionId 一併傳遞,供 ResultScene 顯示 markFactionConquered。
    const resolved = pickBossById(factionId);
    const bossId = resolved?.id ?? factionId;
    const startBattle = () => {
      // 內部 scene.start 不走 setMode → 補發 ef:enter-battle 重掛技能按鈕。
      try { window.dispatchEvent(new CustomEvent('ef:enter-battle')); } catch { /* noop */ }
      try {
        this.scene.start('HorizontalBattleScene', {
          bossId,
          hubFactionId: factionId,
        });
      } catch { /* noop */ }
    };
    try {
      const cam = this.cameras?.main;
      if (cam && typeof (cam as unknown as { fadeOut?: (ms: number, r?: number, g?: number, b?: number) => void }).fadeOut === 'function') {
        (cam as unknown as { fadeOut: (ms: number, r: number, g: number, b: number) => void }).fadeOut(300, 0, 0, 0);
        const once = (cam as unknown as { once?: (evt: string, cb: () => void) => void }).once;
        if (typeof once === 'function') {
          once.call(cam, 'camerafadeoutcomplete', startBattle);
        } else {
          // No fade event → start immediately (headless test path)。
          startBattle();
        }
      } else {
        // No camera → start immediately。
        startBattle();
      }
    } catch {
      // Final fallback — even if fade wiring breaks, still start the scene。
      startBattle();
    }
  }

  /**
   * Polish helper — fade-out 期間 disable 全部 building 的 interactive,防止
   * player 在 300ms 內連點解鎖的兩棟建築結果開到第二棟 boss。
   *
   * 全部 try-guard,headless / partial stub 也安全。
   */
  private _disableAllBuildingInteractive(): void {
    for (const id of Object.keys(this.buildings) as FactionId[]) {
      const b = this.buildings[id];
      if (!b) continue;
      try {
        const c = b.container as unknown as { disableInteractive?: () => void };
        c.disableInteractive?.();
      } catch { /* noop per-entry */ }
    }
  }

  private _showLockedToast(factionId: FactionId): void {
    try { this.toast?.destroy(); } catch { /* noop */ }
    const f = FACTIONS[factionId];
    const msg = `需先在修煉學院將 ${f.displayName} 修煉 box ≥ 3`;
    try {
      this.toast = this.add.text(400, 540, msg, {
        fontSize: '14px',
        fontFamily: 'sans-serif',
        color: '#ffffff',
        backgroundColor: '#7a1c1c',
        padding: { x: 10, y: 6 },
      });
      try { this.toast.setOrigin(0.5); } catch { /* noop */ }
      try { this.toast.setDepth(100); } catch { /* noop */ }
      try {
        this.tweens.add({
          targets: this.toast,
          alpha: 0,
          duration: 2000,
          onComplete: () => { try { this.toast?.destroy(); } catch { /* noop */ } this.toast = null; },
        });
      } catch { /* headless */ }
    } catch { /* headless */ }
  }

  /**
   * 「← 修煉學院」handler — emit navigate:academy + fade out + window redirect。
   * 學習與遊戲分離:此 method 只 navigate,不彈題目。
   */
  onBackToAcademy(): void {
    try { this.events.emit('navigate:academy'); } catch { /* noop */ }
    const redirect = () => {
      try {
        if (typeof window !== 'undefined' && window.location) {
          // 整合層在 main.ts 透過 ef:return-home 事件接收;這裡仍寫 /index.html 為 spec 規範。
          // dispatch ef:return-home 讓 main.ts 的 setMode('home') 接手。
          try { window.dispatchEvent(new Event('ef:return-home')); } catch { /* noop */ }
        }
      } catch { /* noop */ }
    };
    try {
      const cam = this.cameras?.main;
      if (cam && typeof (cam as unknown as { fadeOut?: (ms: number, r?: number, g?: number, b?: number) => void }).fadeOut === 'function') {
        (cam as unknown as { fadeOut: (ms: number, r: number, g: number, b: number) => void }).fadeOut(300, 0, 0, 0);
        const once = (cam as unknown as { once?: (evt: string, cb: () => void) => void }).once;
        if (typeof once === 'function') {
          once.call(cam, 'camerafadeoutcomplete', redirect);
        } else {
          redirect();
        }
      } else {
        redirect();
      }
    } catch { redirect(); }
  }

  /**
   * Mastery W4 — chapter-mastered 響應 stub.
   *
   * `chapterId` 格式 `{subject}.ch{number}`(例如 `'math.ch5'`)。映射到對應
   * faction 後,call `markFactionConquered` 的 building.showConquered() 動畫
   * 作為視覺反饋(W7 ship 後本 method 可延伸為 chapter-level 進度條 / 章節
   * 旗子等更精緻的視覺,本 PR 先 stub 不動 progress)。
   *
   * 學習與遊戲分離鐵則:此 method 由 game/ 側內部呼叫,且 listener 只透過
   * `window.addEventListener('chapter:mastered', …)` 訂閱學習端 CustomEvent。
   */
  markChapterConquered(chapterId: string): void {
    // SPEC §3 example: visual feedback on the corresponding faction building.
    const subjectToFaction: Record<Subject, FactionId> = {
      math: 'celestial',
      chinese: 'runic',
      english: 'techno',
      nature: 'elemental',
      social: 'dominion',
    };
    const dot = chapterId.indexOf('.');
    const subj = dot > 0 ? (chapterId.slice(0, dot) as Subject) : null;
    if (!subj || !(subj in subjectToFaction)) return;
    const faction = subjectToFaction[subj];
    try {
      this.buildings[faction]?.showConquered();
    } catch { /* headless or building not mounted yet */ }
  }

  /**
   * Boss defeat 從 HorizontalBattleScene 返回時呼叫;✓ icon 顯示 + scale punch。
   * 同時 recordBossDefeated 寫入 progress.bossesDefeated。
   */
  markFactionConquered(factionId: FactionId): void {
    const building = this.buildings[factionId];
    try { building?.showConquered(); } catch { /* noop */ }
    try {
      const p = loadProgress();
      if (p) {
        recordBossDefeated(p, factionId);
        saveProgress(p);
      }
    } catch { /* headless / no localStorage */ }
  }

  update(): void {
    try { this.parallax?.update(this.cameras.main.scrollX); } catch { /* headless */ }
  }

  shutdown(): void {
    try { this.parallax?.destroy(); } catch { /* noop */ }
    this.parallax = null;
    try { this.audio?.destroy(); } catch { /* noop */ }
    this.audio = null;
    // Drop juice ref — W8 polish hooks will use it; W7 ships idle ref only。
    void this.juice;
    this.juice = null;
    // Mastery W4 — detach window listener to avoid leaks across scene reloads。
    try {
      if (typeof window !== 'undefined') {
        window.removeEventListener('chapter:mastered', this._chapterMasteredHandler);
      }
    } catch { /* noop */ }
  }
}

// silence "unused" for factionForSubject (kept for future faction-color hooks)。
export const _unused_factionForSubject = factionForSubject;
